import React, { FunctionComponent, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../../../store";
import _, { set } from "lodash";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import Select, { ValueType } from "react-select";
import {
  BasicUserInfo,
  ColorCriteria,
  DataPeriod,
  EvidenceColumn,
  EvidenceType,
  NeedsAreaTag,
  StudentEntry,
} from "../../../../../../store/onboarding/types";
import usePDFGenerate from "../../../../../common/pdf-generate/usePDFGenerate";
import { toastr } from "react-redux-toastr";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { ReadingBucketStructure } from "../../../../../../store/groups/types";


type StudentPaths = {
  [studentId: string]: [string, string, string][];
};

interface DiagnosticMap {
  [key: string]: number[];
}

interface IndicatorData {
  below: number[];
  all_student: number[];
  diagnostic?: DiagnosticMap;
}

interface ClassData {
  class_wide_need: {
    [indicator: string]: IndicatorData;
  };
  content_area_of_need?: Record<string, number[]>;
  total_students?: number[];
}

interface GradeData {
  [className: string]: ClassData;
}

interface TeacherData {
  [grade: string]: GradeData;
}

interface FinalData {
  [teacherName: string]: TeacherData;
}

const DiagAreaMapWithSkillArea: { [key: string]: string } = {
  diag_area_letter_names: "letter_naming",

  diag_area_phonemic_awareness: "phonemic_awareness",
  diag_area_early_phonemic_awareness: "phonemic_awareness",
  phonemic_awareness: "phonemic_awareness",
  early_phonemic_awareness: "phonemic_awareness",

  diag_area_phonics: "phonics",
  phonics: "phonics",

  orf_wrc: "fluency",
  diag_area_fluency: "orf_wrc",
  accuracy: "phonics",
  orf_accuracy: "phonics",
  diag_area_vocab: "comprehension",
  diag_area_comprehension: "comprehension",
};

const areaMapping: { [columnName: string]: string } = {
  letter_naming: "Letter Naming",

  diag_area_phonemic_awareness: "Phonological & Phonemic Awareness",
  diag_area_early_phonemic_awareness: "Phonological & Phonemic Awareness",
  phonemic_awareness: "Phonological & Phonemic Awareness",
  early_phonemic_awareness: "Phonological & Phonemic Awareness",

  diag_area_phonics: "Phonics",
  phonics: "Phonics",

  orf_wrc: "Fluency",
  diag_area_fluency: "Fluency",
  accuracy: "Phonics",
  orf_accuracy: "Phonics",
  comprehension : "Vocabulary and/or Reading Comprehension",
};
const indicatorCategoryMapping: { [columnName: string]: string } = {
  "Letter Naming": "Letter Naming Fluency (LNF)",
  "Phonological & Phonemic Awareness": "Phoneme Segmentation Fluency (PSF)",
  "Phonics": "Nonsense Word Fluency - Correct Letter Sounds (NWF-CLS); Words Recoded Correctly (NWF-WRC); Oral Reading Fluency - Accuracy (ORF-ACC)",
  "Fluency": "Oral Reading Fluency - Words Correct (ORF-WC)",
  "Vocabulary and/or Reading Comprehension": "MAZE"
}

const areaOfNeedSortingArr = [
  'letter naming', 
  'early phonological awareness',
  'phonological awareness', 
  'phonics', 
  'decoding', 
  'sight words',
  'fluency',
  'phonics and/or fluency',
  'vocabulary and/or reading comprehension'
]

const Content: FunctionComponent = () => {

  const [selectedTeacherIDs, setSelectedTeacherIDs] = useState<
      number[] | undefined
  >([]);

  const [reportTableData, setReportTableData] = useState<any>([]);
  const [selectedAllTeacher, setSelectedAllTeacher] = useState<any>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const selectedDataPeriod = useSelector<
    ApplicationState,
    DataPeriod | undefined
  >((s) => s.onboarding.currentDataPeriod);

  const fullEvidenceInDataPeriod = useSelector(
      (s: ApplicationState) => s.onboarding.fullEvidenceInDataPeriod);

  const readingBucketData = useSelector<ApplicationState, ReadingBucketStructure>(
    (s) => s.groups.readingBucket
  );
  const measurementAreaMapping: any =  useMemo(() => { 
    const result: any = {};
    Object.keys(readingBucketData).forEach((grade) => {
      result[grade] = {};
      Object.keys(readingBucketData[grade]).forEach((period) => {
        const arr = readingBucketData[grade][period];
        const periodObj: { [measure: string]: string | null } = {};

        arr.forEach((item) => {
          // Case 1: [category, [measures]]
          if (Array.isArray(item) && typeof item[0] === "string" && Array.isArray(item[1])) {
            const category = item[0];
            item[1].forEach((measure: string) => {
              periodObj[measure] = category;
            });
          }
        });

        result[grade][period] = periodObj;
      });
    });
    return result;
  },[readingBucketData]);

  const assessmentName = useMemo(() => {
    let assessmentName = '';
    if(fullEvidenceInDataPeriod) {
      for (let i = 0; i < fullEvidenceInDataPeriod?.evidence_column_groups.length; i++) {
        const evdenceColumnGroup = fullEvidenceInDataPeriod?.evidence_column_groups[i];
        if(evdenceColumnGroup.is_primary_screening) {
          assessmentName = evdenceColumnGroup.assessment.name;
        }
      }
    }
    return assessmentName;
  },[fullEvidenceInDataPeriod])

  
  const handleReportGenerate = async () => {
    const fileName: string = `${selectedDataPeriod?.name}: Class-Wide Needs.pdf`;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "letter",
    });

    const [PAGE_WIDTH, PAGE_HEIGHT] = [doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight()];
    const MARGIN_LEFT = 10;
    const MARGIN_RIGHT = 10;
    const MARGIN_TOP = 12;
    const MARGIN_BOTTOM = 12;
    const USABLE_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;

    const canvasScale = 2;

    const html2canvasOptions = {
      scale: canvasScale,
      useCORS: true,
      backgroundColor: "#fff",
      width: undefined as number | undefined,
      height: undefined as number | undefined,
    };

    // Ensure fonts are loaded
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    const staticBlock = document.getElementById("static-block");
    if (staticBlock) {
      staticBlock.style.color = "#000";

      // Use scrollWidth/scrollHeight for full content capture
      html2canvasOptions.width = staticBlock.scrollWidth;
      html2canvasOptions.height = staticBlock.scrollHeight;

      const staticCanvas = await html2canvas(staticBlock as HTMLElement, html2canvasOptions);
      const staticBlockData = staticCanvas.toDataURL("image/jpeg", 1.0);

      // Properly scale image to fit usable width
      const imgWidth = USABLE_WIDTH;
      const imgHeight = (imgWidth * staticCanvas.height) / staticCanvas.width;

      let currentPageY = MARGIN_TOP;

      doc.addImage(
        staticBlockData,
        "JPEG",
        MARGIN_LEFT,
        +currentPageY.toFixed(2),
        +imgWidth.toFixed(2),
        +imgHeight.toFixed(2)
      );

      currentPageY += imgHeight + 4;

      const tables = Array.from(document.querySelectorAll("#area-of-need-table #report-table"));
      for (const table of tables) {
        (table as HTMLElement).style.color = "#000";
        (table as HTMLElement).style.imageRendering = "optimizeSpeed";

        html2canvasOptions.width = table.scrollWidth;
        html2canvasOptions.height = table.scrollHeight;

        const canvas = await html2canvas(table as HTMLElement, html2canvasOptions);
        const imgData = canvas.toDataURL("image/jpeg", 1.0);

        const tableImgWidth = USABLE_WIDTH;
        const tableImgHeight = (tableImgWidth * canvas.height) / canvas.width;

        if (currentPageY + tableImgHeight > PAGE_HEIGHT - MARGIN_BOTTOM) {
          doc.addPage();
          currentPageY = MARGIN_TOP;
        }

        doc.addImage(
          imgData,
          "JPEG",
          MARGIN_LEFT,
          +currentPageY.toFixed(2),
          +tableImgWidth.toFixed(2),
          +tableImgHeight.toFixed(2)
        );

        currentPageY += tableImgHeight + 4;
      }

      doc.save(fileName);
    }

    return Promise.resolve();
  };

  
  const {
        teacherIdFilter,
      } = useSelector(
      (s: ApplicationState) => s.onboarding);

  const teachersBySelectedDataPeriod: BasicUserInfo[] | undefined = useMemo(() => {
    const uniqueStudentIds: number[] = []; 
    fullEvidenceInDataPeriod?.evidence_column_groups
    .filter((ecg) => ecg.is_primary_screening || ecg.meta_group_type == 'meta_group_needs')
    .find(
      (ecg) => 
      ecg.evidence_columns.forEach((ec) =>
        ec.student_entries.forEach((se) => {
          if(!uniqueStudentIds.includes(se.student_id)){
            uniqueStudentIds.push(se.student_id);
          }
        })
      )
    );
    return fullEvidenceInDataPeriod?.student_rows
    .filter((sr) => (uniqueStudentIds ?? []).includes(sr.student.id!))
    .reduce(
      (acc, studentRow) => {
        studentRow.student.teachers?.filter(
            (teacher) => teacherIdFilter?.length ? teacherIdFilter.includes(teacher?.id!): true
        ).forEach((teacher) => {
          if (!acc.some((t) => t.id === teacher.id)) {
            acc.push(teacher);
          }
        });
        return acc;
      },
      [] as BasicUserInfo[]); 

  },[fullEvidenceInDataPeriod])

  const handleTeacherDropdownChange =  (selectedTeachers?: ValueType<BasicUserInfo, true>) => {
    const teacherIDs: number[] = selectedTeachers
    ? (selectedTeachers as Array<BasicUserInfo>).map((val) => val.id!)
    : [];
    setSelectedTeacherIDs(teacherIDs);
  };

  const handleStudentPaths = () => {
    const uniqueStudentIds: number[] = []; 
    fullEvidenceInDataPeriod?.evidence_column_groups
    .filter((ecg) => ecg.is_primary_screening || ecg.meta_group_type == 'meta_group_needs')
    .find(
      (ecg) => 
      ecg.evidence_columns.forEach((ec) =>
        ec.student_entries.forEach((se) => {
          if(!uniqueStudentIds.includes(se.student_id)){
            uniqueStudentIds.push(se.student_id);
          }
        })
      )
    );
    return fullEvidenceInDataPeriod?.student_rows
    .filter((sr) => uniqueStudentIds.includes(sr.student.id!))
    .reduce(
      (acc, studentRow) => {
        const studentId = studentRow.student.id!;
        const pathDetails: [string, string, string][] = [];
        let gradeFromStudentEntry: string | undefined = undefined;
        if(!studentRow.grade) {
          const evidenceDataPeriod = fullEvidenceInDataPeriod.evidence_column_groups
          .filter((ecg) => ecg.is_primary_screening)
          .find(
            (ecg) => 
            ecg.evidence_columns.some((ec) =>
              ec.student_entries.some((se) => se.student_id === studentId)
            )
          );
          if(evidenceDataPeriod) {
            evidenceDataPeriod.evidence_columns.forEach((ec) => {
              ec.student_entries.forEach((se) => {
                if(se.student_id === studentId) {
                  gradeFromStudentEntry = se.grade;
                }
              });
            });
          }
        }
    
        studentRow.student.teachers?.forEach((teacher) => {
          if (!selectedAllTeacher ? selectedTeacherIDs?.includes(teacher.id!) : true) {
            const teacherName = `${teacher.id}-${teacher.first_name} ${teacher.last_name}`;
            const grade = studentRow.grade ?? gradeFromStudentEntry!;
            const className = teacher.classes?.length
              ? teacher.classes[0].name
              : "No class assigned";
    
            pathDetails.push([teacherName, grade, className]);
          }
        });
    
        if (pathDetails.length > 0) {
          acc[studentId] = pathDetails;
        }
    
        return acc;
      },
      {} as StudentPaths
    );
  }
  
  const isNonEmpty = (value: string | undefined | null): boolean => {
    return !!value && value.trim() !== "";
  }  

  const convertSetsToArrays = (obj: any): any => {
    if (obj instanceof Set) {
      return Array.from(obj);
    } else if (Array.isArray(obj)) {
      return obj.map(convertSetsToArrays);
    } else if (typeof obj === "object" && obj !== null) {
      const newObj: any = {};
      for (const key in obj) {
        newObj[key] = convertSetsToArrays(obj[key]);
      }
      return newObj;
    } else {
      return obj;
    }
  }

  const createReportTableUI = (tableData: FinalData) => {
    const ui: any[] = [];
    for (const teacher in tableData) {
      const grades = tableData[teacher];
      const teacherName = teacher?.split("-")[1];

      for (const grade in grades) {
        const classes = grades[grade];

        for (const className in classes) {
          if (!className || className.trim().length === 0) continue;

          const classData = classes[className];
          const indicators = classData.class_wide_need;
          const indicatorIdsContent: string[] = [];
          const filteredIndicators: [string, IndicatorData][] = [];
          const contentAreaOfNeeds: any[] = [];
          const totalStudents = classData.total_students ? classData.total_students.length : 0;
          if (indicators && Object.keys(indicators).length === 0 && Object.keys(contentAreaOfNeeds).length === 0) continue;
          if (totalStudents === 0) continue; // Skip if no students in class
          if (classData.content_area_of_need) {
            let contentAreaOfNeedsRow: any[] = [];
            for (const needGroup in classData.content_area_of_need) {
              const students = classData.content_area_of_need[needGroup];
              const cleanedNeedGroup = needGroup.replaceAll("'", "");
              contentAreaOfNeedsRow = [
                ...contentAreaOfNeedsRow,
                {
                  "groupData": cleanedNeedGroup,
                  "studentsCount": students.length,
                  "totalStudents": totalStudents,
                }
              ]
            }
            contentAreaOfNeedsRow.sort((a, b) => {
              return a.groupData.toLowerCase() < b.groupData.toLowerCase() ? -1 : 1;
            }).forEach((needGroup, index) => {
                contentAreaOfNeeds.push(
                  <tr key={index}>
                    <td colSpan={2}>{needGroup.groupData}</td>
                    <td><strong>{needGroup.studentsCount}</strong> out of <strong>{needGroup.totalStudents}</strong> student(s)</td>
                  </tr>
                )
            });
          }

          for (const indicator in indicators) {
            const data = indicators[indicator];
            if ((data.below.length / (data.all_student.length || 1)) >= 0.5) {
              filteredIndicators.push([indicator, data]);
            } else {
              filteredIndicators.push([indicator, data]);
            }
          }

          const isClassWideNeedWithDiag = filteredIndicators.length  && filteredIndicators.some(([indicator, data]) => {
            const isClassWideNeedGenerate = (data.below.length / (data.all_student.length || 1)) >= 0.5;
            const isDiagnostics = data.diagnostic && Object.keys(data.diagnostic).length > 0;
            return isClassWideNeedGenerate || isDiagnostics;
          })

          ui.push(
            <div key={`${teacherName}-${grade}-${className}`} className="mb-5" id="report-table">
              <h3 className="pdfFontFamily mb-2">[Grade-{grade?.includes('G') ? grade.replace('G',''): grade}] Class-Wide Intervention Needs</h3>
              <Table bordered>
                <thead className="bg-light">
                  <tr>
                    <th colSpan={3}>
                      <div className="pdf-label-block">
                        <strong>Teacher:</strong> <span className="font-weight-bold">{teacherName}</span>
                      </div>
                      <div className="pdf-label-block">
                        <strong>Class:</strong> <span className="font-weight-bold">{className}</span>
                      </div>
                    </th>
                  </tr>
                  <tr><th colSpan={3} className="text-center">Class-Wide Intervention Needs</th></tr>
                  {isClassWideNeedWithDiag ? <tr>
                    <th>Screening Indicator</th>
                    <th>Class-Wide Intervention Need</th>
                    <th>Diagnostic Skill Information</th>
                  </tr> : ''}
                </thead>
                <tbody>
                  { isClassWideNeedWithDiag ?
                      filteredIndicators.filter(([indicator, data]) => {
                         const isClassWideNeedGenerate = (data.below.length / (data.all_student.length || 1)) >= 0.5;
                        const isDiagnostics = data.diagnostic && Object.keys(data.diagnostic).length > 0;
                        return isClassWideNeedGenerate || isDiagnostics;
                      }).map(([indicator, data], index) => {
                        const total = data.all_student.length;
                        const count = data.below.length;
                        const isClassWideNeedGenerate = (data.below.length / (data.all_student.length || 1)) >= 0.5;
                        return (
                          <tr key={indicator}>
                            <td width={"33%"}>
                              <strong>{indicator}</strong>
                              <br />
                              <small>e.g., <span className="align-bottom">{indicatorCategoryMapping[indicator]}</span></small>
                            </td>
                            <td width={"33%"}>
                              {isClassWideNeedGenerate ? (
                                <>
                                  <span style={{ color: "red", fontSize: "1.4rem", fontWeight:"bold", marginRight:"3px" }}>&#33;</span>
                                  <span>
                                    <strong>{count}</strong> out of <strong>{total}</strong> student(s) below/well below benchmark 
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="text-muted">
                                    Class-wide intervention not identified. Continue small-group intervention planning as needed.
                                  </span>
                                  {/* <span style={{ color: "green", verticalAlign:"top", fontSize: "1.4rem", fontWeight:"bold", marginRight:"3px" }}>*</span> */}
                                </>
                              )}
                            </td>
                            <td width={"33%"}>
                              {data.diagnostic && Object.keys(data.diagnostic).length > 0 ? (
                                <>
                                  {Object.entries(data.diagnostic).map(([key, ids]) => (
                                    <div key={key}>
                                      • <strong>{key}</strong> ({ids.length} out of {total} student(s))
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <span className="text-muted">
                                  Diagnostics have not been entered.
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    : (
                      <tr>
                        <td colSpan={3} className="text-center" width={"100%"}>
                          <span className="text-muted">
                            No class-wide intervention needs identified.
                          </span>
                        </td> 
                      </tr>
                    )
                  }
                </tbody>
                {classData.content_area_of_need && (
                  <>
                    <thead className="bg-light">
                      <tr><th colSpan={3}  className="text-center">Content Area(s) of Need</th></tr>
                    </thead>
                    <tbody>
                      {contentAreaOfNeeds.length ? contentAreaOfNeeds : (
                        <tr>
                          <td colSpan={3} className="text-center">
                            <span className="text-muted">
                              No content area of need identified.
                            </span>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </>
                )}
              </Table>
            </div>
          );
        }
      }
    }
    setReportTableData(ui);
    return Promise.resolve(ui);
  }

  const tableDataStructure = (studentPaths: StudentPaths) => {
    const tableData: any = {};
    Object.entries(studentPaths!).forEach(([, paths]) => {
      paths.forEach((path) => {
        if (!isNonEmpty(path[0]) || !isNonEmpty(path[1]) || !isNonEmpty(path[2])) return;
    
        const [teacher, grade, classes] = path;
    
        if (!tableData[teacher]) {
          tableData[teacher] = {};
        }
        if (!tableData[teacher][grade]) {
          tableData[teacher][grade] = {};
        }
        if (!tableData[teacher][grade][classes]) {
          tableData[teacher][grade][classes] = {};
        }
      });
    });
    return tableData;
  }

  const createDiagnosticOrAreaOfNeedData = (type: string) => {
    const diagnosticData: any = {};
    const areaOfNeedData: Record<string, string> = {};

    fullEvidenceInDataPeriod?.evidence_column_groups.forEach((ecg) => {
      if(ecg.meta_group_type == "meta_group_needs") {
        ecg.evidence_columns.forEach((ec) => {
          if ((ec.evidence_type === EvidenceType.DiagnosticInfo) && (type == 'diagnostic')) {
            ec.student_entries.forEach((entry: StudentEntry) => { 
              const tags = (entry.diagnostic_tags ?? []).map((d) => d);
              const uniqueTags = tags.filter((tag, index) => tags.indexOf(tag) === index);
              diagnosticData[entry.student_id] = uniqueTags.map((tag) => ({
                display_name: tag.display_name,
                diagnostic_area: tag.area
              }));
            })
          } else if ((ec.evidence_type === EvidenceType.NeedAreas) && (type == 'areaOfNeeds')) {
            ec.student_entries.forEach((entry: StudentEntry) => { 
              const tags = entry.needs_area_tags || [];
              let areaPs: string | undefined;
              if (tags.length === 3 || tags.length === 4) {
                const names = tags.map((tag) => tag.display_name);
                if(names.length) {
                  if (['G4','G5','G6','G7','G8'].includes(entry.grade!) &&
                      names.includes('Phonics') && names.includes('Fluency')){
                      const filteredArray = names.filter((name)=> !['Fluency', 'Phonics'].includes(name));
                      areaPs = `Phonics and/or Fluency | ${[...filteredArray].sort((a, b) => areaOfNeedSortingArr.indexOf(a.toLowerCase()) - areaOfNeedSortingArr.indexOf(b.toLowerCase())).join(', ')}`;
                                
                  } else {
                  //for dibils data and custom assessment
                    if((fullEvidenceInDataPeriod?.evidence_column_groups[0].assessment != null) 
                      && ((fullEvidenceInDataPeriod?.evidence_column_groups[0].assessment.special_case == null)
                        || fullEvidenceInDataPeriod?.evidence_column_groups[0].assessment.special_case?.includes('dibels'))) {
                        let nameArray1:string[] = [];
                        let nameArray2:string[] = [];
                        let nameArray3:string[] = [];
                        let nameArray4:string[] = [];
                        names.forEach((name, index) => {
                          if(['Phonics','Decoding','Sight Words'].includes(name)) {
                            nameArray1.push(name);
                          } 
                          if(['Fluency'].includes(name)) {
                            nameArray2.push(name);
                          }  
                          if(['Phonological Awareness'].includes(name)) {
                            nameArray3.push(name);
                          }
                          if(['Letter Names'].includes(name)) {
                            nameArray4.push(name);
                          }
                        })
                        let nameStr1: string = nameArray1.length ? nameArray1.sort((a, b) => areaOfNeedSortingArr.indexOf(a.toLowerCase()) - areaOfNeedSortingArr.indexOf(b.toLowerCase())).join(', '): '';
                        let nameStr2: string = nameArray2.length ? (nameArray1.length ? " | ": '') + nameArray2.sort((a, b) => areaOfNeedSortingArr.indexOf(a.toLowerCase()) - areaOfNeedSortingArr.indexOf(b.toLowerCase())).join(', '): '';
                        let nameStr3: string = nameArray3.length ? (nameArray2.length ? " | ": '') + nameArray3.sort((a, b) => areaOfNeedSortingArr.indexOf(a.toLowerCase()) - areaOfNeedSortingArr.indexOf(b.toLowerCase())).join(', '): '';
                        let nameStr4: string = nameArray4.length ? (nameArray3.length ? " | ": '') + nameArray4.sort((a, b) => areaOfNeedSortingArr.indexOf(a.toLowerCase()) - areaOfNeedSortingArr.indexOf(b.toLowerCase())).join(', '): '';
                        areaPs = `${nameStr1} ${nameStr2} ${nameStr3} ${nameStr4}`;

                    } else {
                      // for behavior data
                      const displayNames = names.sort((a, b) => areaOfNeedSortingArr.indexOf(a.toLowerCase()) - areaOfNeedSortingArr.indexOf(b.toLowerCase()));
                      areaPs = `${displayNames.join(', ')} ${displayNames.length ? " | ": ""}`;
                    }
                  }
                }
              } else {
                areaPs = tags.sort((a: NeedsAreaTag, b: NeedsAreaTag) => areaOfNeedSortingArr.indexOf(a.display_name.toLowerCase()) - areaOfNeedSortingArr.indexOf(b.display_name.toLowerCase())).slice(0, 2).map((tag) => {
                  return tag.display_name;
                }).join(' | ');
                // Special condition because we don't have Phonics and/or Fluency area of needs as of now.
                const tagArray = tags.map((tag)=> tag.display_name);
                if (['G4','G5','G6','G7','G8'].includes(entry.grade!) &&
                    tagArray.includes('Phonics') && tagArray.includes('Fluency')){
                    areaPs = 'Phonics and/or Fluency';
                }
              }

              // const tags = (entry.needs_area_tags ?? []).map((d) => d.display_name);
              // const uniqueTags = tags.filter((tag, index) => tags.indexOf(tag) === index);
              areaOfNeedData[entry.student_id] = areaPs!;
            })
          }
        })
      }
    });
    return {diagnosticData, areaOfNeedData};
  }

  const handleCreatePDF = () => { 
    // Inputs
    let studentPaths: StudentPaths | undefined = {};
    const time_period: string | undefined = selectedDataPeriod?.time_period; 
    // Output
    let tableData: any = {};

    studentPaths = handleStudentPaths();

    // Step 1: Build basic nested structure from student paths
    tableData = tableDataStructure(studentPaths!);
    // Step 2: Build diagnostic data
    const { diagnosticData } = createDiagnosticOrAreaOfNeedData('diagnostic');
    
    // Step 3: Build AON data
    const { areaOfNeedData } = createDiagnosticOrAreaOfNeedData('areaOfNeeds');

    // Step 4: Populate tableData with area-based information
    fullEvidenceInDataPeriod?.evidence_column_groups.forEach((ecg) => {
      if(ecg.is_primary_screening) {
        ecg.evidence_columns.forEach((ec: EvidenceColumn) => {
          ec.student_entries.forEach((st: StudentEntry, index) => {
            const paths = studentPaths?.[st.student_id]; 
            if (paths) {
              paths.forEach((path) => { 
                const [teacher, grade, classes] = path;
                const area = areaMapping[measurementAreaMapping?.[grade]?.[time_period!]?.[ec.measurement?.known_assessment!]];
                if(area) {
                  const basicPath = tableData[teacher][grade][classes] || {};
                  if(!basicPath['class_wide_need']) {
                    basicPath['class_wide_need'] = {};
                  }
                  if(!basicPath['content_area_of_need']) {
                    basicPath['content_area_of_need'] = {};
                  }
                  if (!basicPath['total_students']) {
                    basicPath['total_students'] = new Set<string>();
                  }

                  if (!basicPath['class_wide_need'][area]) {
                    basicPath['class_wide_need'][area] = {};
                  }
            
                  if (!basicPath['class_wide_need'][area]["below"]) {
                    basicPath['class_wide_need'][area]["below"] = new Set<string>();
                  }
            
                  if (!basicPath['class_wide_need'][area]["all_student"]) {
                    basicPath['class_wide_need'][area]["all_student"] = new Set<string>();
                  }
            
                  if (!basicPath['class_wide_need'][area]["diagnostic"]) {
                    basicPath['class_wide_need'][area]["diagnostic"] = {};
                  }

                  const benchmarks = ec.measurement.grade_specific_color_criteria?.[grade]?.[time_period!] || [];
                  let category: "below" | "other" | null = null;
                  const filteredBenchmarks = benchmarks?.filter((b) => b !== null) || [];
                  if (filteredBenchmarks.length) {
                    for (let i = 0; i < filteredBenchmarks.length; i++) {
                      const b: ColorCriteria = filteredBenchmarks[i];
                      const val = parseInt(st.value!, 10);
                      if (val >= b.range_min! && val <= b.range_max!) {
                        if (b.benchmark_status!.toLowerCase().includes("below")) {
                          category = "below";
                          break;
                        }
                      }
                    }
                  }

                  if (category === "below") {
                    basicPath['class_wide_need'][area]["below"]?.add(st.student_id);
                  } 
                  basicPath['class_wide_need'][area]["all_student"]?.add(st.student_id);
                  

                  const diags = diagnosticData[st.student_id];
                  if (diags) {
                    diags.forEach((d: any) => {
                      const diagnosticArea = DiagAreaMapWithSkillArea[d.diagnostic_area]
                      if (ec.measurement.skill_areas?.includes(diagnosticArea)) {
                        if (!basicPath['class_wide_need'][area]["diagnostic"]![d.display_name]) {
                          basicPath['class_wide_need'][area]["diagnostic"]![d.display_name] = new Set<string>();
                        }
                        basicPath['class_wide_need'][area]["diagnostic"]![d.display_name].add(st.student_id);
                      }
                    });
                  }
                  const student_areas = areaOfNeedData[st.student_id];
                  
                  if (student_areas) {
                    //const key = `'${student_areas?.sort((a: string, b: string) => areaOfNeedSortingArr.indexOf(a.toLowerCase()) - areaOfNeedSortingArr.indexOf(b.toLowerCase())).join("', '")}'`;
                    if (!basicPath['content_area_of_need'][student_areas]) {
                      basicPath['content_area_of_need'][student_areas] = new Set<string>();
                    }
                    basicPath['content_area_of_need'][student_areas].add(st.student_id);
                  }
                  
                  
                  if('total_students' in basicPath) {
                    basicPath['total_students'].add(st.student_id);
                  } 

                  tableData[teacher][grade][classes] = basicPath;
                }
              })
            }

          });
        })
      }
    });
    tableData = convertSetsToArrays(tableData);
    if(tableData && Object.keys(tableData).length) {
      // Step 5: Set report table data
      setLoading(true);
      createReportTableUI(tableData)
      .then(() => {
        // Step 6: Generate PDF after UI is set 
        handleReportGenerate().then(() => {
          setLoading(false);
          toastr.success("Success", "Report generated successfully.");
        }).catch((error) => {
          setLoading(false);
        });
      })
      .catch((error) => {
        toastr.error("Failed", "Failed to generate report table data: " + error.message);
        setLoading(false);
      });
    } else {
      toastr.error("Failed", "No data available for the selected teacher(s).");
      setLoading(false);
      return;
    }
  }

  
  return (
    <div>
      <div className="d-flex row py-1">
        <div className="col-12">
          <Form.Check 
            type="checkbox"
            className="pointer"
            id="sortSwitch"
            label="All Teachers"
            checked={selectedAllTeacher ?? false}
            onChange={(e) => {
              const checked = e.target.checked;
              if(checked) {
                setSelectedTeacherIDs([]);
              }
              setSelectedAllTeacher(checked);
            }}
          />
        </div>
      </div>
      <h3 className="mb-3">Select the teachers.</h3>
      <Select
        isMulti
        name="schools"
        value={teachersBySelectedDataPeriod?.filter((teacher) =>
          selectedTeacherIDs?.find((selectedTeacherId) => selectedTeacherId === teacher.id)
        )}
        getOptionLabel={({ first_name, last_name }: BasicUserInfo) => `${first_name} ${last_name}`}
        getOptionValue={({ id }: BasicUserInfo) => id!.toString()}
        options={teachersBySelectedDataPeriod}
        onChange={handleTeacherDropdownChange}
        placeholder="Select..."
        isDisabled={selectedAllTeacher}
      />  
      <div className="d-flex justify-content-around flex-wrap">
        <Button
          bsPrefix="b"
          className="blueBtnSm text-nowrap mt-3"
          onClick={handleCreatePDF}
          disabled={(!selectedTeacherIDs?.length && !selectedAllTeacher) || loading}
        >
          {loading ? <><Spinner animation="border" size="sm" /> Downloading PDF...</> : 'Download PDF'}
          
        </Button>
      </div>
      {reportTableData.length ? (
        <div 
        id="area-of-need-table"
        className="area-of-need-table-max-height-wrapper" 
        style={{
          position: "absolute",
          left: "-9999px",
          top: "0",
          width: "210mm",
        }}
        >
          <div id="static-block">
            <div
              className="only-for-pdf"
              
              style={{
                background: "#00265b",
                padding: "0.5rem 1rem",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "1rem",
                borderRadius: "0.5rem",
              }}
            >
              <img
                src="/media/readycoach-logo.png"
                alt="ReadyCoach"
                style={{ height: "2rem" }}
              />
            </div>
            {selectedDataPeriod && (
              <h2 className="mb-3 pdfFontFamily">Data Period: {selectedDataPeriod.name}{assessmentName !== '' ? ', '+assessmentName : ''}</h2>
            )}
            <p className="pdfFontFamily">
              This report summarizes class-wide intervention needs based on key screening indicators. It is designed to support planning when a large proportion of students share common intervention needs, but resources for small-group instruction are limited. Each section highlights needs by grade, teacher, and class, including a breakdown of the number of students requiring intervention.
            </p>
            <p className="pdfFontFamily">
              If a large proportion of students are below or well below benchmark on a screening indicator, a red exclamation point will signal the need to consider class-wide intervention. If class-wide intervention is not indicated, a message will state that class-wide needs are not identified, and teams can proceed with small-group intervention planning as needed.
            </p>
            <p className="pdfFontFamily">
              When class-wide intervention is identified, diagnostic skill information will be displayed based on the data entered for students during the selected data period. Additional data sources, such as curriculum-embedded assessments, may also be used to identify skill needs for class-wide intervention.
            </p>
            <p className="pdfFontFamily">
              ReadyCoach also provides Content Areas of Need, based on the combined performance of two critical screening indicators for the given grade and time period. These areas offer additional insight to support both class-wide and small-group intervention planning.
            </p>
          </div>
          <div 
          className="pdfFontFamily"
          >{reportTableData}</div>
        </div>
      ) : ''}
    </div>
  );
};

export default Content;
